import React, { useEffect, useState } from 'react';
import { useConfirm } from '@hevoio/hevo-ui-toolkit';
import MapperAPI from '../../../../containers/schema-mapper/api/MapperAPI';
import useToaster from '../../../../toaster/hooks/useToaster';
import { DELETE_CONFIRM_BASE } from '../../../../shared/constants/deleteConfirm';
import { SchemaResourceType } from '../../interfaces/schemaExplorerFolder';

import styles from './SchemaExplorerFolder.module.scss';
import useClipboardService from '../../../../clipboard/hooks/useClipboardService';
import { getFullTableName, getQuoteType } from '../../utils/schema-explorer-tree.helper';
import { SchemaExplorerFolderProps } from '../../interfaces/schemaExplorer';
import SchemaExplorerFile from '../schema-explorer-file/SchemaExplorerFile';
import RelationGroupTrigger from '../relation-group-trigger/RelationGroupTrigger';
import Relations from '../relations/Relations';
import { HdIcon, HdIconButton, HdMenu, HdRbacMenuItem, HdTooltip } from '../../../../components';
import { DestinationStrings } from '../../../../containers/destination/constants/strings';
import { RbacPermissions } from '../../../../core/interfaces/user';
import { getDataIdGenerator } from '../../../../core/utils/generateDataId';

const inlineRelationCount = 3;

export const dataIdGeneratorSchemaExplorerFolder = getDataIdGenerator('schema-explorer-folder');

export function SchemaExplorerFolder({
  folder,
  source,
  onFolderDelete,
  onFolderToggle,
  schemaExplorerUIOptions
}: SchemaExplorerFolderProps) {
  const [collapsed, setCollapsed] = useState(folder.collapsed);
  const isSchemaResourceTypeTable = folder.resourceType === SchemaResourceType.TABLE;
  const clipboardService = useClipboardService();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [folderNameFocused, setFolderNameFocused] = useState(false);

  const toaster = useToaster();
  const { confirm } = useConfirm();

  useEffect(() => {
    setCollapsed(folder.collapsed);
  }, [folder.collapsed]);

  const toggleFolder = event => {
    if (event.targetDirective) {
      return;
    }
    /**
     *  We are checking the event's source to decide whether to toggle the collapse state or not.
     *  Any event.target with data attr "stoppropagate" will be ignored and collapse state wont' be toggled.
     */

    const allElementsWithStopPropagation = event.currentTarget.querySelectorAll(
      '[data-stoppropagate="true"]'
    );

    if (allElementsWithStopPropagation?.length) {
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < allElementsWithStopPropagation.length; i++) {
        if (allElementsWithStopPropagation[i].contains(event.target)) {
          return;
        }
      }
    }

    setCollapsed(!collapsed);
    onFolderToggle(!collapsed);
  };

  const getClipboardContent = (folderName: string) => {
    const {
      Database: databaseName,
      Schema: schemaName,
      'Project Id': projectId,
      Dataset: datasetName
    } = source.display_config;
    const quoteType = getQuoteType(source.dest_type, datasetName);
    return getFullTableName(
      folderName,
      databaseName || projectId,
      schemaName || datasetName,
      quoteType
    );
  };

  const copyToClipboard = (event, folderName) => {
    event.stopPropagation();
    clipboardService.copy(getClipboardContent(folderName), 'Table Name copied to clipboard');
  };

  const onMenuClicked = () => {
    setIsMenuOpen(prev => !prev);
  };

  const onFolderDeleteCallback = async () => MapperAPI.dropTable(source.id, folder.name);

  const onFolderDeleteSuccessful = response => {
    // The provider runs this on a cancel too, handing back no response.
    if (response?.success) {
      toaster.pop('success', undefined, DestinationStrings.deleteTableSuccess(folder.name));
      onFolderDelete(folder.name);
    }
  };

  // Same dialog the Models delete uses, so the two look identical: spread the
  // shared delete styling and supply only this table's own copy.
  const onDeleteFolder = event => {
    event.stopPropagation();
    setIsMenuOpen(false);

    confirm({
      ...DELETE_CONFIRM_BASE,
      title: DestinationStrings.deleteTableTitle,
      body: DestinationStrings.deleteTableBody(folder.name),
      positiveButtonText: DestinationStrings.deleteTablePstBtn,
      negativeButtonText: DestinationStrings.deleteTableNgtBtn,
      modalProps: { placement: 'top' },
      onPositiveClickCallback: onFolderDeleteCallback,
      onPositiveClickSuccess: onFolderDeleteSuccessful
    });
  };

  return (
    <>
      <div
        tabIndex={0}
        className={styles.folderInfoContainer}
        onClick={toggleFolder}
        onKeyDown={event => {
          if (event.key === 'Enter') {
            toggleFolder(event);
          }
        }}
        role='button'
        data-id={`schema-folder-${folder.id}-button`}
      >
        <HdIcon
          name='solid-dropdown'
          size={1}
          className={`${styles.folderToggleArrow} ${
            !collapsed ? styles.folderToggleArrowToggled : ''
          }`}
          dataId={dataIdGeneratorSchemaExplorerFolder('solid-dropdown')}
        />

        <span
          className={`hevo-icon ${isSchemaResourceTypeTable ? 'hevo-table' : 'hevo-view'} ${
            styles.folderIcon
          }`}
          data-id={dataIdGeneratorSchemaExplorerFolder('solid-dropdown-icon')}
        />

        <span className={styles.folderNameContainer}>
          <HdTooltip
            dataId={dataIdGeneratorSchemaExplorerFolder('folder-name')}
            title={folder.name}
            enableOnTextOverflow={!!folderNameFocused}
          >
            {/* eslint-disable-next-line jsx-a11y/mouse-events-have-key-events */}
            <span
              className={styles.folderName}
              onMouseOver={() => setFolderNameFocused(true)}
              onMouseOut={() => setFolderNameFocused(false)}
              data-id={dataIdGeneratorSchemaExplorerFolder('tooltip-span')}
            >
              {folder.name}
            </span>
          </HdTooltip>

          <HdIcon
            name='copy'
            onClick={event => copyToClipboard(event, folder.name)}
            className={`copy-sm ${styles.copySm}`}
            tabIndex={-1}
            dataId={dataIdGeneratorSchemaExplorerFolder('copy-icon')}
          />
        </span>

        {schemaExplorerUIOptions?.showRelationsLineage &&
        folder.relationGroups &&
        folder.relationGroups.length ? (
          <div className={styles.relations}>
            {folder.relationGroups.slice(0, inlineRelationCount).map((relationGroup, index) => (
              <React.Fragment key={index}>
                <HdTooltip
                  mode='surface'
                  customStyle={{ maxWidth: 'initial' }}
                  placement='right-start'
                  disableInteractive={false}
                  title={<RelationGroupPopup relationGroup={relationGroup} />}
                  dataId={dataIdGeneratorSchemaExplorerFolder(`relation-group-${index}`)}
                >
                  <div className={styles.relationGroupTrigger}>
                    <RelationGroupTrigger relationGroup={relationGroup} />
                  </div>
                </HdTooltip>
              </React.Fragment>
            ))}
            {folder.relationGroups && folder.relationGroups.length > inlineRelationCount ? (
              <HdTooltip
                mode='surface'
                customStyle={{ maxWidth: 'initial' }}
                placement='right-start'
                disableInteractive={false}
                title={
                  <HiddenRelationsPopup
                    relationGroups={folder.relationGroups.slice(inlineRelationCount)}
                  />
                }
                dataId={dataIdGeneratorSchemaExplorerFolder('hidden-relation-group')}
              >
                <div className={styles.collapsedRelationsTrigger}>
                  +{folder.relationGroups.length - inlineRelationCount}
                </div>
              </HdTooltip>
            ) : null}
          </div>
        ) : null}

        {schemaExplorerUIOptions?.showTableMenuOption && isSchemaResourceTypeTable ? (
          <>
            <HdIconButton
              id={`viewMore${folder.id}`}
              className={`${styles.moreIcon} pure-ripple`}
              onClick={onMenuClicked}
              data-stoppropagate='true'
              dataId={`schema-folder-${folder.id}-menu`}
            >
              <HdIcon
                name='more-vertical'
                size={3}
                dataId={dataIdGeneratorSchemaExplorerFolder('view-more-icon')}
              />
            </HdIconButton>

            <HdMenu
              open={isMenuOpen}
              target={`#viewMore${folder.id}`}
              onClose={() => setIsMenuOpen(false)}
              dataId={dataIdGeneratorSchemaExplorerFolder('viewMoreMenu')}
            >
              <HdRbacMenuItem
                className='error'
                rbacPermission={RbacPermissions.DESTINATION_EDIT}
                onClick={onDeleteFolder}
                dataId={dataIdGeneratorSchemaExplorerFolder('error-menu')}
              >
                <HdIcon name='delete' />
                Delete Table
              </HdRbacMenuItem>
            </HdMenu>
          </>
        ) : null}
      </div>

      {!collapsed ? (
        <div
          className={styles.folderContent}
          data-id={dataIdGeneratorSchemaExplorerFolder('schema-file')}
        >
          {folder.files.map(file => (
            <SchemaExplorerFile
              schemaExplorerUIOptions={schemaExplorerUIOptions}
              key={file.name}
              file={file}
              source={source}
            />
          ))}
        </div>
      ) : null}
    </>
  );
}

function RelationGroupPopup({ relationGroup }) {
  return (
    <>
      <div className='relation-title pb-2 pt-1'>
        <b className='text-uppercase'>Lineages</b>
      </div>

      <hr className='mb-2' />

      <Relations relationGroup={relationGroup} />
    </>
  );
}
function HiddenRelationsPopup({ relationGroups }) {
  return (
    <>
      {relationGroups.map(relationGroup => (
        <Relations key={relationGroup.meta.iconName} relationGroup={relationGroup} />
      ))}
    </>
  );
}
